import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import vexorLogo from '../assets/vexor-logo.png';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/', { replace: true });
    } catch (err) { toast.error(err.response?.data?.detail || 'Login failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="cycles-public-home">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading" data-testid="login-title">DigSense IQ</h1>
            <p className="mt-2 text-sm text-slate-500">Track life. Compare cost. Cut waste.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="form-field">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="bg-slate-50 border-slate-300 focus:bg-white" data-testid="login-email-input" />
            </div>
            <div className="form-field">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="bg-slate-50 border-slate-300 focus:bg-white" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <div className="mt-8 flex flex-col items-center gap-1.5" data-testid="vendor-attribution">
            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Powered by</span>
            <img src={vexorLogo} alt="VEXOR GLOBAL" className="h-5 w-auto opacity-80" />
          </div>
        </div>
      </div>
      <div className="hidden lg:flex lg:flex-1 relative">
        <img src="https://images.unsplash.com/photo-1664578867628-dae621194f4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxvcGVuJTIwcGl0JTIwbWluZSUyMGFlcmlhbHxlbnwwfHx8fDE3NzMyNTc3NTd8MA&ixlib=rb-4.1.0&q=85" alt="Mining Operations" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-bold font-heading mb-3">Optimize Your Mining Operations</h2>
          <p className="text-slate-300 text-sm max-w-md">Track excavator tooth point performance, analyze wear patterns, and reduce operational costs with data-driven insights.</p>
        </div>
      </div>
    </div>
  );
}
